import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  Camera,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Film,
  Image as ImageIcon,
  Pause,
  CloudOff,
  Play,
  RefreshCw,
  RotateCcw,
  RotateCw,
  X
} from 'lucide-react';

interface BranchItem {
  id: string;
  name: string;
  company?: { name: string };
}

interface FlcamFile {
  name: string;
  size: number;
  modifiedAt: string | null;
}

interface FlcamEvent {
  event: number;
  startedAt: string | null;
  opening: FlcamFile | null;
  closing: FlcamFile | null;
  videos: FlcamFile[];
  photos: FlcamFile[];
  log: FlcamFile | null;
}

type ViewerTab = 'photos' | 'video' | 'log';

const PAGE_SIZE = 12;

const formatBytes = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / Math.pow(1024, idx);
  const digits = scaled >= 10 || idx === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${units[idx]}`;
};

// "2026-10-12T14:18:17" (horário do ESP32) -> "12/10/2026 14:18:17"
const formatEventDate = (value: string | null) => {
  if (!value) return '—';
  const [date, time] = value.split('T');
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y} ${time}`;
};

// Tempo entre a foto de abertura e a de fechamento, pelo nome dos arquivos.
const fileTime = (file: FlcamFile | null) => {
  const match = file?.name.match(/_(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})_/);
  return match ? new Date(`${match[1]}T${match[2]}:${match[3]}:${match[4]}`).getTime() : null;
};

const openDuration = (event: FlcamEvent) => {
  const start = fileTime(event.opening);
  const end = fileTime(event.closing);
  if (start === null || end === null || end < start) return null;
  const total = Math.round((end - start) / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const fetchFile = async (vesselId: string, name: string) => {
  const res = await api.get(`/flcam/${vesselId}/file`, { params: { name }, responseType: 'blob' });
  return res.data as Blob;
};

const downloadFile = async (vesselId: string, name: string) => {
  try {
    const blob = await fetchFile(vesselId, name);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    toast.error('Falha ao baixar arquivo');
  }
};

// A câmera fica montada de lado; a rotação (por vessel) é aplicada no navegador.
const drawRotated = (canvas: HTMLCanvasElement, bitmap: ImageBitmap, rotation: number) => {
  const swap = rotation % 180 !== 0;
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
};

const rotateBlob = async (blob: Blob, rotation: number): Promise<Blob> => {
  if (!rotation) return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  drawRotated(canvas, bitmap, rotation);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob((out) => resolve(out || blob), 'image/jpeg', 0.92));
};

const useFileUrl = (vesselId: string, name: string | null | undefined, rotation = 0) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!vesselId || !name) return;
    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    fetchFile(vesselId, name)
      .then((blob) => rotateBlob(blob, rotation))
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [vesselId, name, rotation]);

  return { url, failed };
};

const RemoteImage: React.FC<{
  vesselId: string;
  file: FlcamFile | null;
  className?: string;
  alt: string;
  rotation?: number;
}> = ({ vesselId, file, className, alt, rotation = 0 }) => {
  const { url, failed } = useFileUrl(vesselId, file?.name, rotation);
  if (!file) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 text-gray-400 text-xs`}>
        Sem foto
      </div>
    );
  }
  if (failed) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 text-gray-400 text-xs`}>
        Falha ao carregar
      </div>
    );
  }
  if (!url) {
    return <div className={`${className} bg-gray-100 animate-pulse`} />;
  }
  return <img src={url} alt={alt} className={`${className} object-contain bg-gray-900`} />;
};

// Os vídeos do ESP32 são AVI com quadros MJPEG, que o navegador não reproduz.
// Extraímos os JPEGs do arquivo e tocamos quadro a quadro.
const parseMjpegAvi = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const tag = (pos: number) => String.fromCharCode(bytes[pos], bytes[pos + 1], bytes[pos + 2], bytes[pos + 3]);
  const find = (needle: string, from = 0) => {
    for (let i = from; i < bytes.length - 4; i++) if (tag(i) === needle) return i;
    return -1;
  };

  let fps = 2;
  const avih = find('avih');
  if (avih >= 0 && avih + 12 <= bytes.length) {
    const usPerFrame = view.getUint32(avih + 8, true);
    if (usPerFrame > 0) fps = Math.min(30, Math.max(0.5, 1e6 / usPerFrame));
  }

  const frames: Blob[] = [];
  const movi = find('movi');
  if (movi >= 0) {
    let pos = movi + 4;
    while (pos + 8 <= bytes.length) {
      const id = tag(pos);
      const size = view.getUint32(pos + 4, true);
      if (id === 'LIST') {
        pos += 12;
        continue;
      }
      if (id === 'idx1' || size === 0 || pos + 8 + size > bytes.length) break;
      if (id.endsWith('dc') || id.endsWith('db')) {
        frames.push(new Blob([bytes.subarray(pos + 8, pos + 8 + size)], { type: 'image/jpeg' }));
      }
      pos += 8 + size + (size & 1);
    }
  }

  // Arquivo sem índice/tamanhos válidos: procura os JPEGs pelos marcadores.
  if (frames.length === 0) {
    let start = -1;
    for (let i = 0; i < bytes.length - 1; i++) {
      if (bytes[i] === 0xff && bytes[i + 1] === 0xd8 && bytes[i + 2] === 0xff) start = i;
      else if (start >= 0 && bytes[i] === 0xff && bytes[i + 1] === 0xd9) {
        frames.push(new Blob([bytes.subarray(start, i + 2)], { type: 'image/jpeg' }));
        start = -1;
      }
    }
  }

  return { frames, fps };
};

const MjpegPlayer: React.FC<{ vesselId: string; file: FlcamFile; rotation: number }> = ({
  vesselId,
  file,
  rotation
}) => {
  const [frames, setFrames] = useState<Blob[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [fps, setFps] = useState(2);
  const [speed, setSpeed] = useState(1);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setFrames([]);
    setIndex(0);
    setPlaying(false);
    fetchFile(vesselId, file.name)
      .then((blob) => blob.arrayBuffer())
      .then((buffer) => {
        if (!active) return;
        const parsed = parseMjpegAvi(buffer);
        if (parsed.frames.length === 0) throw new Error('no frames');
        setFrames(parsed.frames);
        setFps(parsed.fps);
        setStatus('ready');
        setPlaying(true);
      })
      .catch(() => active && setStatus('error'));
    return () => {
      active = false;
    };
  }, [vesselId, file.name]);

  useEffect(() => {
    const frame = frames[index];
    if (!frame) return;
    let active = true;
    createImageBitmap(frame)
      .then((bitmap) => {
        if (active && canvasRef.current) drawRotated(canvasRef.current, bitmap, rotation);
        bitmap.close();
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [frames, index, rotation]);

  useEffect(() => {
    if (!playing || frames.length === 0) return;
    const timer = setInterval(() => {
      setIndex((current) => {
        if (current + 1 >= frames.length) {
          setPlaying(false);
          return current;
        }
        return current + 1;
      });
    }, 1000 / (fps * speed));
    return () => clearInterval(timer);
  }, [playing, frames.length, fps, speed]);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-72 text-gray-500 gap-3">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        <p className="text-sm">Baixando vídeo ({formatBytes(file.size)})…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="p-10 text-center text-gray-600 space-y-3">
        <AlertTriangle className="mx-auto text-yellow-500" />
        <p>Não foi possível reproduzir este vídeo no navegador.</p>
        <button
          onClick={() => downloadFile(vesselId, file.name)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
        >
          <Download size={16} /> Baixar arquivo
        </button>
      </div>
    );
  }

  const togglePlay = () => {
    if (!playing && index >= frames.length - 1) setIndex(0);
    setPlaying(!playing);
  };

  return (
    <div className="space-y-3">
      <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center">
        <canvas ref={canvasRef} className="max-h-[60vh] max-w-full" aria-label={`Quadro ${index + 1}`} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={togglePlay}
          className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
          {playing ? 'Pausar' : 'Reproduzir'}
        </button>
        <input
          type="range"
          min={0}
          max={frames.length - 1}
          value={index}
          onChange={(e) => {
            setPlaying(false);
            setIndex(Number(e.target.value));
          }}
          className="flex-1 min-w-[160px]"
        />
        <span className="text-sm text-gray-600 tabular-nums">
          {index + 1} / {frames.length}
        </span>
        <select
          value={speed}
          onChange={(e) => setSpeed(Number(e.target.value))}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          {[0.5, 1, 2, 4].map((value) => (
            <option key={value} value={value}>
              {value}x
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const LogViewer: React.FC<{ vesselId: string; file: FlcamFile; eventNumber: number }> = ({
  vesselId,
  file,
  eventNumber
}) => {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [onlyEvent, setOnlyEvent] = useState(true);
  const preRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    let active = true;
    setText(null);
    setFailed(false);
    fetchFile(vesselId, file.name)
      .then((blob) => blob.text())
      .then((value) => active && setText(value))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [vesselId, file.name]);

  // O log do ESP32 é cumulativo; o trecho do evento vai de "EVENTO N -" até o próximo evento.
  const visibleText = useMemo(() => {
    if (!text || !onlyEvent) return text;
    const lines = text.split(/\r?\n/);
    const start = lines.findIndex((line) => line.includes(`EVENTO ${eventNumber} -`));
    if (start < 0) return text;
    const next = lines.findIndex((line, i) => i > start && /EVENTO \d+ -/.test(line));
    return lines.slice(start, next < 0 ? undefined : next).join('\n');
  }, [text, onlyEvent, eventNumber]);

  useEffect(() => {
    if (!onlyEvent && preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [visibleText, onlyEvent]);

  if (failed) {
    return <p className="p-6 text-center text-gray-600">Falha ao carregar o log.</p>;
  }
  if (text === null) {
    return <div className="h-72 bg-gray-100 rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {[true, false].map((value) => (
          <button
            key={String(value)}
            onClick={() => setOnlyEvent(value)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              onlyEvent === value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {value ? `Somente evento ${eventNumber}` : 'Log completo'}
          </button>
        ))}
      </div>
      <pre
        ref={preRef}
        className="bg-gray-900 text-gray-100 text-xs leading-relaxed rounded-lg p-4 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all"
      >
        {visibleText}
      </pre>
    </div>
  );
};

const EventViewer: React.FC<{
  vesselId: string;
  event: FlcamEvent;
  initialTab: ViewerTab;
  rotation: number;
  onRotate: (rotation: number) => void;
  onClose: () => void;
}> = ({ vesselId, event, initialTab, rotation, onRotate, onClose }) => {
  const [tab, setTab] = useState<ViewerTab>(initialTab);
  const photos = [event.opening, event.closing, ...event.photos].filter(Boolean) as FlcamFile[];
  const files = [...photos, ...event.videos, ...(event.log ? [event.log] : [])];
  const duration = openDuration(event);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs: { id: ViewerTab; label: string; icon: React.ElementType; enabled: boolean }[] = [
    { id: 'photos', label: 'Fotos', icon: ImageIcon, enabled: photos.length > 0 },
    { id: 'video', label: 'Vídeo', icon: Film, enabled: event.videos.length > 0 },
    { id: 'log', label: 'Log', icon: FileText, enabled: Boolean(event.log) }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Evento {event.event}</h2>
            <p className="text-sm text-gray-500">
              {formatEventDate(event.startedAt)}
              {duration ? ` · rack aberto por ${duration}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 pt-4 flex flex-wrap items-center gap-2">
          {tabs.map(({ id, label, icon: Icon, enabled }) => (
            <button
              key={id}
              disabled={!enabled}
              onClick={() => setTab(id)}
              className={`px-3 py-1.5 rounded-lg text-sm border flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                tab === id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
          {tab !== 'log' && (
            <div className="ml-auto flex items-center gap-1">
              <span className="text-xs text-gray-500 mr-1">Girar</span>
              <button
                onClick={() => onRotate((rotation + 270) % 360)}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                title="Girar 90° para a esquerda"
              >
                <RotateCcw size={15} />
              </button>
              <button
                onClick={() => onRotate((rotation + 90) % 360)}
                className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                title="Girar 90° para a direita"
              >
                <RotateCw size={15} />
              </button>
            </div>
          )}
        </div>

        <div className="p-5">
          {tab === 'photos' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {photos.map((photo) => (
                <figure key={photo.name} className="space-y-2">
                  <RemoteImage
                    vesselId={vesselId}
                    file={photo}
                    alt={photo.name}
                    rotation={rotation}
                    className="w-full rounded-lg h-[55vh]"
                  />
                  <figcaption className="text-xs text-gray-500">
                    {photo === event.opening ? 'Abertura' : photo === event.closing ? 'Fechamento' : photo.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
          {tab === 'video' && event.videos[0] && (
            <div className="space-y-6">
              {event.videos.map((video) => (
                <MjpegPlayer key={video.name} vesselId={vesselId} file={video} rotation={rotation} />
              ))}
            </div>
          )}
          {tab === 'log' && event.log && (
            <LogViewer vesselId={vesselId} file={event.log} eventNumber={event.event} />
          )}
        </div>

        <div className="px-5 pb-5">
          <p className="text-sm font-medium text-gray-700 mb-2">Arquivos</p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {files.map((file) => (
              <li key={file.name} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="truncate text-gray-700">{file.name}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="text-gray-500">{formatBytes(file.size)}</span>
                  <button
                    onClick={() => downloadFile(vesselId, file.name)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Baixar"
                  >
                    <Download size={16} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Flcam: React.FC = () => {
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [events, setEvents] = useState<FlcamEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [viewer, setViewer] = useState<{ event: FlcamEvent; tab: ViewerTab } | null>(null);
  const [rotation, setRotation] = useState(0);
  const [online, setOnline] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const loadBranches = useCallback(async () => {
    try {
      const res = await api.get('/branches');
      const data = res.data || [];
      setBranches(data);
      if (data.length > 0) {
        setSelectedVesselId((current) => current || data[0].id);
      }
    } catch (err) {
      toast.error('Falha ao carregar vessels');
    }
  }, []);

  const loadEvents = useCallback(async (vesselId: string) => {
    if (!vesselId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/flcam/${vesselId}/events`);
      setEvents(res.data?.events || []);
      setRotation(res.data?.settings?.rotation ?? 0);
      setOnline(res.data?.online !== false);
      setLastSyncAt(res.data?.lastSyncAt ?? null);
      setPage(0);
    } catch (err: any) {
      setEvents([]);
      setError(err?.response?.data?.error || 'Falha ao carregar o FLCAM deste vessel.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (selectedVesselId) loadEvents(selectedVesselId);
  }, [loadEvents, selectedVesselId]);

  const saveRotation = async (value: number) => {
    const previous = rotation;
    setRotation(value);
    try {
      await api.put(`/flcam/${selectedVesselId}/settings`, { rotation: value });
    } catch {
      setRotation(previous);
      toast.error('Falha ao salvar a rotação');
    }
  };

  const pageCount = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const pageEvents = events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">FLCAM</h1>
          <p className="text-sm text-gray-500">
            Fotos, vídeos e logs de abertura do rack registrados pela câmera do vessel.
          </p>
        </div>
        <button
          onClick={() => loadEvents(selectedVesselId)}
          className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 self-start"
          disabled={loading || !selectedVesselId}
        >
          <RefreshCw size={16} />
          Atualizar
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Vessel</label>
        <select
          value={selectedVesselId}
          onChange={(e) => setSelectedVesselId(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {branches.length === 0 && <option value="">Nenhum vessel disponível</option>}
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name} {branch.company?.name ? `- ${branch.company.name}` : ''}
            </option>
          ))}
        </select>
        {!loading && !error && events.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">{events.length} eventos registrados</p>
        )}
        {!loading && !error && !online && (
          <p className="text-xs text-amber-700 mt-2 flex items-center gap-1.5">
            <CloudOff size={14} />
            Vessel sem conexão no momento. Exibindo os arquivos já copiados para o servidor
            {lastSyncAt ? ` (última cópia em ${new Date(lastSyncAt).toLocaleString('pt-BR')})` : ''}.
          </p>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-600 space-y-3">
          <AlertTriangle className="mx-auto text-yellow-500" />
          <p>{error}</p>
          <p className="text-xs text-gray-500">Verifique se este vessel possui câmera FLCAM instalada.</p>
          <button
            onClick={() => loadEvents(selectedVesselId)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Tentar novamente
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500 space-y-2">
          <Camera className="mx-auto text-gray-400" />
          <p>Nenhum evento registrado neste vessel.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {pageEvents.map((event) => {
              const duration = openDuration(event);
              return (
                <div key={event.event} className="bg-white rounded-lg shadow overflow-hidden flex flex-col">
                  <button
                    onClick={() => setViewer({ event, tab: event.opening || event.closing ? 'photos' : 'video' })}
                    className="block"
                    title="Abrir evento"
                  >
                    <RemoteImage
                      vesselId={selectedVesselId}
                      file={event.opening || event.closing}
                      alt={`Evento ${event.event}`}
                      rotation={rotation}
                      className="w-full aspect-[4/3]"
                    />
                  </button>
                  <div className="p-4 flex-1 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-800">Evento {event.event}</p>
                        <p className="text-sm text-gray-500">{formatEventDate(event.startedAt)}</p>
                      </div>
                      {duration && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full whitespace-nowrap">
                          Aberto {duration}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-auto">
                      <button
                        onClick={() => setViewer({ event, tab: 'photos' })}
                        disabled={!event.opening && !event.closing && event.photos.length === 0}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-40"
                      >
                        <ImageIcon size={15} /> Fotos
                      </button>
                      <button
                        onClick={() => setViewer({ event, tab: 'video' })}
                        disabled={event.videos.length === 0}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-40"
                      >
                        <Film size={15} /> Vídeo
                      </button>
                      <button
                        onClick={() => setViewer({ event, tab: 'log' })}
                        disabled={!event.log}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-40"
                      >
                        <FileText size={15} /> Log
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40"
                aria-label="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-600">
                Página {page + 1} de {pageCount}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
                className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40"
                aria-label="Próxima página"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {viewer && (
        <EventViewer
          vesselId={selectedVesselId}
          event={viewer.event}
          initialTab={viewer.tab}
          rotation={rotation}
          onRotate={saveRotation}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
};

export default Flcam;
